import type { RefObject, UIEvent } from 'react';
import { LiveTranscriptionRole as LiveTranscriptRole } from 'src/graphql/entities/sidekick/sidekick.operations';

export const LIVE_TRANSCRIPTION_FALLBACK_POLL_INTERVAL_MS = 5_000;
export const LIVE_TRANSCRIPTION_SEGMENTS_LIMIT = 100;
export const LIVE_TRANSCRIPTION_PARAGRAPH_GAP_SECONDS = 0.2;
export { LiveTranscriptRole };

export interface LiveTranscriptSegment {
  id: string;
  role: LiveTranscriptRole;
  timestamp: string;
  transcriptionText: string;
  start?: number;
  duration?: number;
}

export interface LiveTranscriptionState {
  segments: LiveTranscriptSegment[];
  isStreaming: boolean;
  isFinished: boolean;
  transcriptionId: string | null;
  externalTranscriptionId: string | null;
}

export interface UseLiveTranscriptionInput {
  enabled: boolean;
}

export interface UserSegmentEventPayload {
  type: 'RealTimeUserTranscriptionSegment';
  transcriptionText: string;
  role: LiveTranscriptRole;
  timestamp: string;
  partnerId: number;
  userId: string;
  transcriptionId: string;
  externalTranscriptionId: string;
  start?: number;
  duration?: number;
}

export interface UserFinishedEventPayload {
  type: 'RealTimeUserTranscriptionFinished';
  partnerId: number;
  userId: string;
  transcriptionId: string;
  externalTranscriptionId?: string;
}

export interface NormalizedLiveTranscriptionPayload {
  transcriptionId: string;
  externalTranscriptionId: string;
  isFinished: boolean;
  cursor: string | null;
  segments: {
    role: LiveTranscriptRole;
    timestamp: string;
    transcriptionText: string;
    start?: number | null;
    duration?: number | null;
  }[];
}

export interface LiveTranscriptDrawerProps {
  enabled: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

export interface LiveTranscriptHeaderProps {
  isCollapsed: boolean;
  isFinished: boolean;
  isStreaming: boolean;
  canCopy: boolean;
  canSearch: boolean;
  onExpandFromHeader: () => void;
  onToggleCollapse: () => void;
  onCopyTranscript: () => void;
  onOpenSearch: () => void;
}

export interface LiveTranscriptSearchHeaderProps {
  searchQuery: string;
  totalMatches: number;
  currentMatchIndex: number;
  onSearchQueryChange: (query: string) => void;
  onPrevMatch: () => void;
  onNextMatch: () => void;
  onCloseSearch: () => void;
}

export interface LiveTranscriptMessagesProps {
  groupedSegments: LiveTranscriptSegment[];
  isStreaming: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  onCopySegment: (text: string) => void;
  searchQuery: string;
  currentMatchIndex: number;
  matchOffsetBySegmentId: Map<string, number>;
}

export interface LiveTranscriptMessageProps {
  segment: LiveTranscriptSegment;
  showSpeakerChange: boolean;
  onCopy: (text: string) => void;
  searchQuery: string;
  matchOffset: number;
  currentMatchIndex: number;
}

export interface LiveTranscriptSegmentWithDisplayMeta {
  segment: LiveTranscriptSegment;
  showSpeakerChange: boolean;
}
